import React, { Reducer, useCallback, useEffect, useMemo, useReducer } from 'react';
import { Button, Chip, Typography } from '@mui/material';
import { useAuthContext } from 'AuthContext';
import reducer, {
  initialState,
  IPCheckTableAction,
  IPCheckTableActionType,
  IPCheckTableState,
} from './IPCheckTableReducer';
import Table from 'components/shared/Table';
import { VoteValueType } from 'store/enums/VoteTypes';
import { Attendee, Vote } from 'store/types/Meeting';
import { SortConfig, TableColumn } from 'store/types/Table';
import { ipCheckValueColor } from 'util/meeting';

import styles from './IPCheckTable.module.scss';

interface IPCheckTableProps {
  disableVoting: boolean;
  voters: Attendee[];
  votesByAttendeeId: Map<string, Vote>;
  castVote: (attendee: Attendee, value: VoteValueType) => void;
}

const IPCheckTable: React.FunctionComponent<IPCheckTableProps> = ({
  disableVoting,
  voters,
  votesByAttendeeId,
  castVote,
}) => {
  const { isStaff } = useAuthContext();
  const [{ list = [], sort }, dispatch] = useReducer<Reducer<IPCheckTableState, IPCheckTableAction>>(
    reducer,
    initialState
  );

  useEffect(() => {
    if (voters) {
      dispatch({
        type: IPCheckTableActionType.SetInitialList,
        payload: { initialList: voters },
      });
    }
  }, [voters]);

  const onSortChange = useCallback((newSort: SortConfig) => {
    dispatch({ type: IPCheckTableActionType.UpdateSort, payload: { sort: newSort } });
  }, []);

  const columns: Array<TableColumn<Attendee>> = useMemo(() => {
    const result: Array<TableColumn<Attendee>> = [
      {
        dataIndex: 'affiliate',
        label: <Typography fontWeight="bold">Voting Interest</Typography>,
        render: (_, record) => <Typography>{record.affiliate}</Typography>,
      },
      {
        dataIndex: 'company',
        label: <Typography fontWeight="bold">Company</Typography>,
        render: (_, record) => <Typography>{record.company}</Typography>,
      },
      {
        dataIndex: 'name',
        label: <Typography fontWeight="bold">Name</Typography>,
        render: (_, record) => <Typography>{record.name}</Typography>,
      },
      {
        key: 'vote',
        align: 'center',
        label: <Typography fontWeight="bold">Response</Typography>,
        render: (_, record) => {
          const showVotingOptions = record.isManuallyAdded && isStaff;
          const existingVote = votesByAttendeeId.get(record.id)?.value;

          return showVotingOptions ? (
            <div className={styles.buttonGroup}>
              <Button
                color={existingVote === VoteValueType.Yes ? 'secondary' : 'info'}
                variant={existingVote === VoteValueType.Yes ? 'contained' : 'outlined'}
                disabled={disableVoting}
                onClick={() => castVote(record, VoteValueType.Yes)}
              >
                {VoteValueType.Yes}
              </Button>
              <Button
                color={existingVote === VoteValueType.No ? 'secondary' : 'info'}
                variant={existingVote === VoteValueType.No ? 'contained' : 'outlined'}
                disabled={disableVoting}
                onClick={() => castVote(record, VoteValueType.No)}
              >
                {VoteValueType.No}
              </Button>
            </div>
          ) : existingVote ? (
            <Chip color={disableVoting ? 'default' : ipCheckValueColor[existingVote]} label={existingVote} />
          ) : (
            <Chip label={'Waiting on answer'} />
          );
        },
      },
    ];

    return result;
  }, [castVote, disableVoting, isStaff, votesByAttendeeId]);

  return (
    <Table
      className={styles.table}
      columns={columns}
      list={list}
      sort={sort}
      onSortChange={onSortChange}
      noResultsText="No current attendance"
    />
  );
};

export default IPCheckTable;
